<script>
import CommentCompanyResponse from '@comp/document/comments/CommentCompanyResponse.vue'
import CommentEditImportance from '@comp/document/comments/CommentEditImportance.vue'
import CommentEditImportanceOmanType from '@comp/document/comments/CommentEditImportanceOmanType.vue'
import ImageEditor from '@comp/document/comments/ImageEditor.vue'
import TextEditor from '@comp/document/comments/TextEditor.vue'
import { STEP_EXTERNAL_CONTRIBUTIONS } from '@src/utils/consts'

export const MODE = Object.freeze({
  show: 'show',
  edit: 'edit',
})

export default {
  name: 'CommentEditable',
  components: {
    CommentCompanyResponse,
    CommentEditImportance,
    CommentEditImportanceOmanType,
    TextEditor,
    ImageEditor,
  },
  props: {
    comment: {
      type: Object,
      default: () => {},
    },
    isFirstComment: {
      type: Boolean,
      required: true,
    },
    isEditable: {
      type: Boolean,
      default: () => true,
    },
    isEditionMode: {
      type: Boolean,
      default: () => false,
    },
    currentStep: {
      type: String,
      default: () => '',
    },
    showButtons: {
      type: Boolean,
      default: () => true,
    },
    isOmanType: {
      type: Boolean,
      required: true,
    },
    pdfPreview: {
      type: Boolean,
      required: true,
    },
    commentedPage: {
      type: Number,
      required: true,
    },
    isCommentedPageClickedProps: {
      type: Boolean,
      required: true,
    },
  },
  emits: [
    'showImage',
    'startEdit',
    'cancelEdit',
    'saveEdit',
    'activateLink',
    'delete',
  ],
  data () {
    return {
      mode: MODE.show,
      canCreateInternalNotes: true,
      importance: '',
      newText: '',
      newImage: {
        url: '',
        file: null,
      },
      errorMsg: [],
      isLinkActivated: false,
      isCommentedPageClicked: false,
      commentedPageData: {
        pageNumber: 0,
      },
    }
  },
  computed: {
    MODE: () => MODE,
    canEdit () {
      return this.isEditable && this.comment.permissions.canEdit
    },
    canDelete () {
      return this.isEditable && this.comment.permissions.canDelete
    },
    isExternalContributionsStep () {
      return this.currentStep === STEP_EXTERNAL_CONTRIBUTIONS
    },
    externalCommentWarning () {
      return this.$gettext('Warning: comments written at this step won\'t be reused in next versions of the document.')
    },
  },
  watch: {
    commentedPage: {
      handler () {
        this.commentedPageData.pageNumber = this.commentedPage || this.comment.commentedPage || 0
      },
      immediate: true,
    },
    isEditionMode: {
      handler (value) {
        if (value) {
          this.edit()
        } else {
          this.show()
        }
      },
      immediate: true,
    },
    isFirstComment: {
      handler () {
        if (this.isFirstComment) {
          this.edit()
        }
      },
      immediate: true,
    },
    isCommentedPageClickedProps () {
      this.isCommentedPageClicked = this.isCommentedPageClickedProps
      if (this.isCommentedPageClicked) {
        this.isLinkActivated = false
        this.$emit('activateLink', this.isLinkActivated)
      }
    },
  },
  methods: {
    showImage () {
      this.$emit('showImage', this.comment)
    },
    edit () {
      this.importance = this.isFirstComment ? '' : null
      this.newText = this.comment.text
      this.newImage = {
        url: this.comment.imageUrl,
        file: null,
      }
      this.errorMsg = []
      this.mode = MODE.edit
      this.$emit(
        'startEdit',
        this.comment,
        this.onCancelClick,
        this.onSaveClick,
        this.isSaveEnabled,
      )
    },
    show () {
      this.newText = ''
      this.newImage.url = ''
      this.mode = MODE.show
    },
    isSaveEnabled () {
      return true
    },
    onCancelClick () {
      this.mode = MODE.show
      this.$emit('cancelEdit', this.comment)
      this.isCommentedPageClicked = false
    },
    validate () {
      this.errorMsg = []
      if (this.isFirstComment && !this.importance) {
        this.errorMsg.push(this.$gettext('The comment importance has not been filled'))
      }
      if (!this.newText.length) {
        this.errorMsg.push(this.$gettext('The text comment is missing'))
      }
      if (this.isOmanType && this.isFirstComment && this.importance.length > 5) {
        this.errorMsg.push(this.$gettext('5 characters maximum'))
      }
      return this.errorMsg.length === 0
    },
    onSaveClick () {
      if (this.validate()) {
        const oldComment = { ...this.comment }
        const newComment = { ...this.comment }
        newComment.text = this.newText
        newComment.imageUrl = this.newImage.url
        newComment.imageFile = this.newImage.file
        this.$emit('saveEdit', newComment, oldComment, this.importance, this.commentedPageData.pageNumber)
        this.mode = MODE.show
      }
    },
    onDelete () {
      this.$emit('delete', this.comment)
    },
    onLinkToDocument () {
      this.isCommentedPageClicked = false
      this.isLinkActivated = true
      this.$emit('activateLink', this.isLinkActivated)
    },
    onImportanceChange (importance) {
      this.importance = importance
    },
    onTextChange (text) {
      this.newText = text
    },
    onImageChange (image) {
      this.newImage.url = image.url
      this.newImage.file = image.file
    },
  },
}
</script>
<template>
  <div v-if="!isEditable || mode === MODE.show">
    <CommentCompanyResponse
      v-if="comment.__typename === 'CompanyResponse'"
      :comment="comment"
    />
    <v-card
      v-else
      class="editable-comment"
    >
      <v-card-title class="title">
        {{ $gettext("Comment created at index ") }} {{ comment.documentVersion.index }}
        <v-btn
          v-if="comment.imageUrl"
          size="small"
          color="secondary"
          variant="elevated"
          prepend-icon="fas fa-paperclip"
          @click="showImage"
        >
          {{ $gettext('Show illustration') }}
        </v-btn>
      </v-card-title>
      <v-card-text class="text">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="comment.text" />
      </v-card-text>
      <div v-if="commentedPageData.pageNumber">
        <div class="linked">
          {{ $gettext("Link inside document") }}
        </div>
        <div class="number">
          {{ $gettext("Page") }} {{ commentedPageData.pageNumber }}
        </div>
      </div>
      <v-card-actions
        class="justify-end show-icons"
      >
        <v-tooltip
          location="end"
          color="secondary"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-if="canEdit"
              color="secondary"
              variant="text"
              size="small"
              v-bind="props"
              icon="fas fa-pen"
              @click="edit"
            />
          </template>
          {{ $gettext('Edit') }}
        </v-tooltip>
        <v-tooltip
          location="end"
          color="secondary"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              v-if="canDelete"
              color="secondary"
              variant="text"
              size="small"
              v-bind="props"
              icon="fas fa-trash"
              @click="onDelete"
            />
          </template>
          {{ $gettext('Delete') }}
        </v-tooltip>
      </v-card-actions>
    </v-card>
  </div>
  <div
    v-else
    class="flex-editable"
  >
    <div class="head-actions">
      <div class="comment-edit-importance">
        <CommentEditImportance
          v-if="isFirstComment && !isOmanType"
          :importance-prop="importance"
          :can-create-internal-notes="canCreateInternalNotes"
          @change="onImportanceChange"
        />
        <CommentEditImportanceOmanType
          v-else-if="isFirstComment && isOmanType"
          :importance-prop="importance"
          @change="onImportanceChange"
        />
      </div>
      <div class="link-actions">
        <v-btn
          v-if="isFirstComment"
          color="primary"
          class="link-btn"
          prepend-icon="fas fa-link"
          :disabled="!pdfPreview"
          @click="onLinkToDocument"
        >
          {{ $gettext('Link inside document') }}
        </v-btn>
        <div class="linked-page-info">
          <span v-if="isCommentedPageClicked">
            {{ $gettext('Link to page: %{ commentedPage }', { commentedPage: commentedPageData.pageNumber}) }}
          </span>
          <span v-else-if="isLinkActivated">
            {{ $gettext('To link your comment to a page, click anywhere on the relevant page.') }}
          </span>
        </div>
      </div>
    </div>
    <div
      v-if="isExternalContributionsStep"
      class="warning-message-section warning-message"
    >
      <v-icon
        size="small"
        class="warning-message"
        icon="fas fa-exclamation-circle"
      />
      {{ externalCommentWarning }}
    </div>
    <div class="fake-card">
      <div class="text-and-image">
        <TextEditor
          class="text-editor"
          :text-prop="newText"
          @change="onTextChange"
        />
        <ImageEditor
          class="image-editor"
          :image="newImage"
          @change="onImageChange"
        />
      </div>
      <div
        v-if="showButtons"
        class="comment-edit-buttons"
      >
        <v-btn
          color="lightgrey"
          prepend-icon="fas fa-times"
          @click="onCancelClick"
        >
          {{ $gettext('Cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          :disabled="!isSaveEnabled"
          prepend-icon="fas fa-save"
          @click="onSaveClick"
        >
          {{ $gettext('Save') }}
        </v-btn>
      </div>
    </div>
    <ul
      v-for="(item, i) in errorMsg"
      :key="i"
      class="error-msg"
    >
      <li>{{ item }}</li>
    </ul>
  </div>
</template>
<style lang="scss" scoped>
.link-btn {
  margin-right: 10px;
}
.comment-edit-importance {
  flex: 2 1 0;
}
.flex-editable {
  display: flex;
  flex-direction: column;
  background-color: $white;
}
.text-and-image {
  align-items: flex-start;
  display: flex;
  flex-direction: row;
}
.link-actions {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5em;
}
.linked-page-info {
  min-height: 2em;
}
.head-actions {
  padding-top: 2%;
  display : flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}
.comment-edit-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-right: 10px;
}
.fake-card {
  display: flex;
  flex: 1 1 100%;
  flex-direction: column;
  margin: 10px 5px;
  padding-top: 5px;
  padding-bottom: 10px;
  box-shadow: 0 0 4px 0 rgb(0 0 0 / 40%);
  border-radius: 5px;
}
.text-editor {
  flex-grow: 1;
  flex-shrink: 1;
  max-width: 65%;
}
.image-editor {
  flex-grow: 1;
  flex-shrink: 1;
  max-width: 30%;
  height: 100%;
  min-height: 250px;
  margin: 0 20px;
  overflow: auto;
}
.error-msg {
  color: $error-red;
}
.editable-comment {
  padding: 5px 15px;
  margin: 5px 0;
  display: grid;
  grid-template:
    "title linked showIcons" 45px
    "text number ." auto;
  overflow-y: hidden;
}
.number {
  grid-area: number;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  font-size: 14px !important;
}
.linked {
  grid-area: linked;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  font-size: 14px !important;
  padding-top: 5px;
  font-weight: 500;
}
.title {
  grid-area: title;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  padding: 0 20px !important;
  font-size: 14px !important;
}
.show-icons {
  grid-area: showIcons;
}
.show-icons .v-icon {
  margin-left: 0.5em;
}
.text {
  grid-area: text;
  color: $grey-medium;
  padding: 0 20px !important;
}
.warning-message {
  color: $warning-orange;
}
.warning-message-section {
  margin-bottom: 10px;
}
</style>
